import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Building2, KeyRound, Plus } from "lucide-react"
import { License, LicenseCompany, useCreateLicense, useLicenseCompanies, useSaveLicenseCompany } from "@/hooks/useLicenses"

interface Props {
  isOpen: boolean
  onClose: (generatedKey?: string, clientName?: string) => void
  license?: License | null
}

const emptyCompany = {
  legal_name: "", trade_name: "", document: "", state_registration: "", email: "", phone: "", whatsapp: "",
  responsible_name: "", responsible_email: "", responsible_phone: "", postal_code: "", street: "", street_number: "",
  address_complement: "", neighborhood: "", city: "", state: "", notes: "",
}

export const LicenseModal = ({ isOpen, onClose, license }: Props) => {
  const { data: companies = [] } = useLicenseCompanies()
  const saveCompany = useSaveLicenseCompany()
  const createLicense = useCreateLicense()
  const [companyId, setCompanyId] = useState("")
  const [showCompany, setShowCompany] = useState(false)
  const [company, setCompany] = useState(emptyCompany)
  const [form, setForm] = useState({ licenseType: "trial" as "trial" | "subscription" | "perpetual", plan: "Avaliação", days: 15, maxDevices: 1, maxOfflineDays: 7, graceDays: 3, notes: "" })

  useEffect(() => {
    if (!isOpen) return
    setCompanyId(license?.company_id || "")
    setShowCompany(false)
    setCompany(emptyCompany)
    setForm({ licenseType: "trial", plan: "Avaliação", days: 15, maxDevices: 1, maxOfflineDays: 7, graceDays: 3, notes: "" })
  }, [isOpen, license])

  const field = (name: keyof typeof emptyCompany, label: string, placeholder = "") => (
    <div className="space-y-1.5"><Label>{label}</Label><Input value={company[name]} placeholder={placeholder}
      onChange={(event) => setCompany((current) => ({ ...current, [name]: event.target.value }))} /></div>
  )

  const handleSaveCompany = async () => {
    const result = await saveCompany.mutateAsync(company)
    setCompanyId(result.company.id)
    setShowCompany(false)
  }

  const handleGenerate = async () => {
    if (!companyId) return
    const result = await createLicense.mutateAsync({ companyId, ...form })
    onClose(result.key, result.license.company?.trade_name || result.license.company?.legal_name || result.license.client_name)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" />Gerar licença vinculada à empresa</DialogTitle>
          <DialogDescription>A chave ficará armazenada permanentemente no cadastro da empresa selecionada.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-xl border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4" />Empresa responsável pela licença</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowCompany(!showCompany)}><Plus className="h-4 w-4 mr-1" />Nova empresa</Button>
            </div>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger><SelectValue placeholder="Selecione a empresa (obrigatório)" /></SelectTrigger>
              <SelectContent>{companies.filter((item) => item.status === "active").map((item) => (
                <SelectItem key={item.id} value={item.id}>{item.trade_name || item.legal_name}{item.document ? ` · ${item.document}` : ""}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>

          {showCompany && <div className="rounded-xl border border-primary/30 p-4 space-y-4">
            <div><h3 className="font-semibold">Cadastro completo da empresa</h3><p className="text-xs text-muted-foreground">Os campos podem ser complementados posteriormente, mas a razão social é obrigatória.</p></div>
            <div className="grid md:grid-cols-2 gap-3">{field("legal_name", "Razão social *", "Empresa Exemplo Ltda")}{field("trade_name", "Nome fantasia")}{field("document", "CNPJ / CPF")}{field("state_registration", "Inscrição estadual")}{field("email", "E-mail financeiro")}{field("phone", "Telefone")}{field("whatsapp", "WhatsApp")}{field("responsible_name", "Responsável")}{field("responsible_email", "E-mail do responsável")}{field("responsible_phone", "Telefone do responsável")}{field("postal_code", "CEP")}{field("street", "Logradouro")}{field("street_number", "Número")}{field("address_complement", "Complemento")}{field("neighborhood", "Bairro")}{field("city", "Cidade")}{field("state", "UF")}</div>
            <div className="space-y-1.5"><Label>Observações da empresa</Label><Textarea value={company.notes} onChange={(event) => setCompany((current) => ({ ...current, notes: event.target.value }))} /></div>
            <Button type="button" onClick={handleSaveCompany} disabled={!company.legal_name.trim() || saveCompany.isPending}>{saveCompany.isPending ? "Salvando…" : "Salvar e selecionar empresa"}</Button>
          </div>}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tipo de licença</Label><Select value={form.licenseType} onValueChange={(value: typeof form.licenseType) => setForm((current) => ({ ...current, licenseType: value, plan: value === "trial" ? "Avaliação" : "Profissional" }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="trial">Avaliação</SelectItem><SelectItem value="subscription">Assinatura</SelectItem><SelectItem value="perpetual">Perpétua com validade técnica</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Plano</Label><Input value={form.plan} onChange={(event) => setForm((current) => ({ ...current, plan: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Validade em dias</Label><Input type="number" min={1} max={3650} value={form.days} onChange={(event) => setForm((current) => ({ ...current, days: Number(event.target.value) }))} /></div>
            <div className="space-y-1.5"><Label>Máximo de computadores</Label><Input type="number" min={1} max={50} value={form.maxDevices} onChange={(event) => setForm((current) => ({ ...current, maxDevices: Number(event.target.value) }))} /></div>
            <div className="space-y-1.5"><Label>Dias permitidos sem internet</Label><Input type="number" min={1} max={90} value={form.maxOfflineDays} onChange={(event) => setForm((current) => ({ ...current, maxOfflineDays: Number(event.target.value) }))} /></div>
            <div className="space-y-1.5"><Label>Tolerância financeira (dias)</Label><Input type="number" min={0} max={90} value={form.graceDays} onChange={(event) => setForm((current) => ({ ...current, graceDays: Number(event.target.value) }))} /></div>
          </div>
          <div className="space-y-1.5"><Label>Observações da licença</Label><Textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>Cancelar</Button>
          <Button onClick={handleGenerate} disabled={!companyId || createLicense.isPending}>{createLicense.isPending ? "Gerando com segurança…" : "Gerar e guardar chave"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
